/**
 * Turns a flat list of reference options into an antd TreeSelect tree, linking
 * nodes by a "child code" (e.g. `termId`) and a "parent code"
 * (e.g. `parents[].parent`).
 *
 * Single-parent model: a term is placed under the first of its parents that
 * exists in the loaded set, so every node appears exactly once and its tree
 * `value` can be the instance id directly (which keeps `treeCheckable` and the
 * submitted value trivial). Terms with no resolvable parent become roots.
 */

export interface ITreeOption {
  id: string; // instance id — the value submitted
  label: string;
  modelId?: string;
  version?: number;
  childCode: string; // this node's own code (e.g. termId)
  parentCodes: string[]; // codes of this node's parents (e.g. parents[].parent)
  selectable: boolean;
  meta?: any; // full transformer output, carried onto the value's `_meta`
}

export interface ITreeSelectNode {
  title: string;
  value: string; // instance id
  selectable: boolean;
  disabled: boolean; // non-selectable nodes are disabled but still expandable
  children?: ITreeSelectNode[];
}

export const buildReferenceTree = (
  options: ITreeOption[],
  leavesOnly = false
): ITreeSelectNode[] => {
  const byChildCode = new Map<string, ITreeOption>();
  options.forEach(o => {
    if (o.childCode) byChildCode.set(o.childCode, o);
  });

  const resolveParentCode = (o: ITreeOption): string | undefined =>
    (o.parentCodes || []).find(
      code => code && code !== o.childCode && byChildCode.has(code)
    );

  const childrenByParent = new Map<string, ITreeOption[]>();
  const roots: ITreeOption[] = [];

  options.forEach(o => {
    const parentCode = resolveParentCode(o);
    if (!parentCode) {
      roots.push(o);
      return;
    }
    const list = childrenByParent.get(parentCode) ?? [];
    list.push(o);
    childrenByParent.set(parentCode, list);
  });

  const buildNode = (
    option: ITreeOption,
    ancestry: Set<string>
  ): ITreeSelectNode => {
    // Cycle guard: stop recursing into a code already on this path.
    const nextAncestry = new Set(ancestry).add(option.childCode);
    const childOptions = (childrenByParent.get(option.childCode) ?? []).filter(
      child => !nextAncestry.has(child.childCode)
    );

    const isLeaf = childOptions.length === 0;
    // `leavesOnly` makes parent nodes expand-only; the per-node `selectable`
    // (from selectableExpression) still applies on top.
    const selectable = option.selectable && (!leavesOnly || isLeaf);

    const node: ITreeSelectNode = {
      title: option.label,
      value: option.id,
      selectable,
      disabled: !selectable,
    };

    if (!isLeaf) {
      node.children = childOptions.map(child => buildNode(child, nextAncestry));
    }

    return node;
  };

  return roots.map(root => buildNode(root, new Set()));
};
